import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function IsleOfWightCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Isle of Wight Pothole Claims | Island Roads PFI & 41,901 Repairs | Fixtyer"
        description="The Isle of Wight runs a £726m Island Roads PFI with no DfT spend rating. U-road RED condition rose 22-fold since 2020, yet the DfT awards GREEN for condition. See what this means for your pothole claim."
        canonical="/councils/isle-of-wight"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Isle of Wight', url: '/councils/isle-of-wight' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'The Isle of Wight has a GREEN condition rating — can I still claim?',
            answer: 'Yes. The DfT Condition scorecard is GREEN because network-wide RED shares sit below England averages — but your claim turns on the specific defect that damaged your vehicle, not borough-wide averages. The council\'s own data shows U-road RED condition climbing from 0.2% in 2020 to 4.4% in 2022, and 9,715 potholes were filled in 2024/25 alone. Section 58 depends on whether your road was reasonably inspected and the defect repaired in time.',
          },
          {
            question: 'Why is there no DfT Spend rating for the Isle of Wight?',
            answer: 'Highways maintenance runs through a 25-year Highways PFI Contract with Island Roads Services Ltd, which began on 1 April 2013. The council pays a single aggregated monthly Unitary Charge covering street lighting, cleaning, resurfacing, pothole repairs and dozens of other services. The council states it "do[es] not have an individual figure for costs incurred for operations" and that Ringway Island Roads expenditure is commercially sensitive. The DfT cannot score spend against allocation when no comparable capital and revenue breakdown is published.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 419.5km — 51.2% of the Isle of Wight\'s 819.48km adopted road network. Unclassified RED condition rose from 0.2% in 2020 to 4.4% in 2022 before settling at 3.0% in 2024 — a 15-fold rise from the 2020 baseline. SCANNER and SCRIM surveys run annually on carriageways where possible, with Detailed Visual Inspection covering roads not surveyed by SCANNER. Prior reports of the same defect through Island Roads\' FixMyStreet system are critical evidence of actual notice.',
          },
          {
            question: 'Island Roads filled nearly 10,000 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. The council recorded 9,715 pothole repairs in 2024/25 — down from a peak of 10,140 in 2023/24. Over five years the total is 41,901 fills. The council forecasts 8,380 repairs in 2025/26 based on the five-year average. That volume is evidence of a network still producing defects at scale, even after 559km of carriageway and footway has been rebuilt or resurfaced since 2013 under the PFI.',
          },
          {
            question: 'Does the Island Roads PFI contract help or hurt my claim?',
            answer: 'Both, depending on your road. The PFI imposes output specifications, annual SCANNER/SCRIM surveys and a documented Highways Asset Management Plan — a structured maintenance system the council can cite under Section 58. But the council cannot break out pothole repair spend from a £726 million bundled contract, and reactive geological works on Leeson Road, Gill Cliff Road and Belgrave Road have already exceeded £1.0 million in projected expenditure. A long-term PFI with bundled payments weakens public transparency on whether your specific road received adequate reactive maintenance.',
          },
          {
            question: 'Who do I claim against — the council or Island Roads?',
            answer: 'Liability rests with Isle of Wight Council as the highway authority under the Highways Act 1980. Island Roads and Ringway Island Roads maintain the network under contract, but the council remains legally responsible for Section 41 maintenance duties. Island Roads publishes its own inspection-frequency guidance and a claims form, but your statutory claim is against the council. A professional claim pack addresses the council and cites the council\'s own transparency report data.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Isle of Wight Council"
        councilSlug="isle-of-wight"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/isle-of-wight"
        headline="Isle of Wight Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Isle of Wight</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="text-brand-200 text-sm font-medium">Not rated</span> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Isle of Wight: U-Road RED Share Up 22-Fold Since 2020
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            The Isle of Wight outsources its entire highway network to Island Roads under a
            <strong className="text-white"> £726 million, 25-year PFI</strong> — so the DfT publishes
            <strong className="text-amber-400"> no spend rating at all</strong>. Network condition earns
            <strong className="text-emerald-400"> GREEN</strong>, yet the overall rating is
            <strong className="text-amber-400"> AMBER</strong> and U-road RED condition climbed from
            <strong className="text-rose-400"> 0.2% to 4.4%</strong> in just two years. The council still filled
            <strong className="text-rose-400"> 41,901 potholes in five years</strong> — including 10,140 in 2023/24 alone.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">0.2% → 4.4%</div>
            <div className="text-white font-medium mt-2">U-road RED condition in two years</div>
            <div className="text-brand-300 text-sm mt-1">
              Unclassified RED share rose from 0.2% in 2020 to 4.4% in 2022 — a 22-fold increase on
              419.5km of residential roads that make up 51% of the island&apos;s 819.48km network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Island Roads PFI */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why There Is No Spend Rating
          </h2>
          <p className="text-surface-600 mb-8">
            The Isle of Wight&apos;s entire highway network runs through Island Roads — a 25-year PFI that bundles
            every maintenance cost into a single confidential monthly payment
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Payments made by the Council are a single aggregated payment for each month of the 25-year term of the contract. As a result, the council do not have an individual figure for costs incurred for operations."
              source="Isle of Wight Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="The expenditure from RIR is considered to be commercially sensitive and has been kept confidential to not Prejudice any commercial interests. As a result, we do not have a figure for the costs incurred by RIR for individual operations. Therefore, it is not possible to provide a breakdown of the actual expenditure for Capital and Revenue."
              source="Isle of Wight Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Island Roads PFI Contract</h3>
              <p className="text-surface-600 text-sm mb-3">
                On 1 April 2013, Isle of Wight Council entered a 25-year Highways PFI Contract with Island
                Roads Services Ltd (IRSL). Ringway Island Roads (RIR) subcontracts rehabilitation, maintenance,
                management and operation of the island&apos;s highway assets.
              </p>
              <p className="text-surface-600 text-sm">
                The total value of Unitary Charge payments over the contract term — based on an assumed
                inflation rate of 2.5% — is approximately <strong>£726 million</strong>. That single
                monthly payment covers street lighting, resurfacing, pothole repairs, gully cleansing,
                winter maintenance, safety inspections and geotechnical monitoring.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">559km Rebuilt Since 2013</h3>
              <p className="text-amber-800 text-sm mb-3">
                Since the PFI began, Ringway Island Roads has rebuilt or resurfaced over
                <strong> 559 kilometres</strong> of footway and carriageway across the island — roughly
                68% of the 819.48km adopted road network.
              </p>
              <p className="text-amber-800 text-sm">
                The council states this PFI rehabilitation has kept the percentage of roads considered for
                maintenance below England averages. Yet 41,901 pothole fills in five years show defects still
                form at scale on the remaining and resurfaced network.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The Network Under Contract</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">819.48km</div>
                <div className="text-brand-300 text-sm">Adopted carriageways</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">700.69km</div>
                <div className="text-brand-300 text-sm">Footways</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">825km</div>
                <div className="text-brand-300 text-sm">Public rights of way</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Plus 12,000 streetlights, 250 retaining walls, bridges, drainage assets, traffic signals and
              <span className="text-amber-400"> 28.15km of cycleways</span> — all maintained under one bundled
              PFI payment the public cannot audit line by line.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and SCRIM survey data from the council&apos;s own transparency report —
            strong headline scores, but U-road RED trending sharply upward from a near-zero baseline
          </p>

          {/* Network size */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Network size (km)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">123.77</div>
                <div className="text-sm text-surface-600">A roads (15.1%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">276.46</div>
                <div className="text-sm text-surface-600">B and C roads (33.8%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">419.5</div>
                <div className="text-sm text-amber-800">U roads (51.2%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">819.48</div>
                <div className="text-sm text-surface-600">Total roads</div>
              </div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (123.77km): stable, low RED</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">0.60%</td>
                    <td className="px-4 py-2 text-center">11.00%</td>
                    <td className="px-4 py-2 text-center">88.00%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.50%</td>
                    <td className="px-4 py-2 text-center">12.00%</td>
                    <td className="px-4 py-2 text-center">86.00%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.40%</td>
                    <td className="px-4 py-2 text-center">12.50%</td>
                    <td className="px-4 py-2 text-center">86.10%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1.80%</td>
                    <td className="px-4 py-2 text-center">13.30%</td>
                    <td className="px-4 py-2 text-center">84.90%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">1.30%</td>
                    <td className="px-4 py-2 text-center font-bold">12.10%</td>
                    <td className="px-4 py-2 text-center font-bold">86.60%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED has stayed below 2% for five years. Collection and reporting is undertaken on an
              annual financial-year frequency.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">B and C roads (276.46km): low RED, rising amber</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">0.70%</td>
                    <td className="px-4 py-2 text-center">15.00%</td>
                    <td className="px-4 py-2 text-center">84.00%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.20%</td>
                    <td className="px-4 py-2 text-center">17.00%</td>
                    <td className="px-4 py-2 text-center">82.00%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.80%</td>
                    <td className="px-4 py-2 text-center">18.10%</td>
                    <td className="px-4 py-2 text-center">80.20%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1.60%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">19.40%</td>
                    <td className="px-4 py-2 text-center">79.00%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">1.26%</td>
                    <td className="px-4 py-2 text-center font-bold">17.41%</td>
                    <td className="px-4 py-2 text-center font-bold">81.33%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 text-sm">
              B/C RED peaked at 1.8% in 2022 while amber hit 19.4% in 2023 — meaning roughly one in five
              B/C roads may need maintenance soon. RED has since fallen to 1.26%.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (419.5km — 51% of network): RED surged then settled</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">0.20%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.80%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.40%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.40%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.00%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              U-road RED climbed from 0.2% to 4.4% between 2020 and 2022 — a 22-fold rise — before easing to
              3.0% in 2024. At 3%, roughly 12.6km of unclassified roads should be considered for maintenance.
              The council notes these figures remain below England averages, but the direction of travel on
              residential streets was sharply upward.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Annual Surveys, Not Continuous Monitoring</h3>
            <p className="text-brand-200 mb-4">
              SCANNER and SCRIM surveys are undertaken annually on carriageways where possible. Detailed
              Visual Inspection covers roads not surveyed by SCANNER. Footways, cycleways and kerbs are walked
              on half the network each year — covering the full network every second contract year. Ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the surveyed half the year your pothole formed?</li>
              <li>• If U-road RED surged to 4.4% in 2022, what was done about your street specifically?</li>
              <li>• Does a GREEN headline scorecard reflect the residential road you were actually driving on?</li>
              <li>• From 2026/27, PAS 2161 will replace the current three-category system — will past data still compare?</li>
            </ul>
            <p className="text-white font-medium">
              A GREEN condition scorecard built on network averages does not mean your specific unclassified
              road was in acceptable condition when you hit the pothole.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole Repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            41,901 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Pothole fill counts from the council&apos;s own transparency report — with a sharp spike in 2023/24
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">7,652</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">7,304</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">7,090</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,140</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">9,715</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">41,901</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~23 Repairs a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Island Roads fills around 23 potholes per day across the island.
                In 2023/24 that peaked at roughly 28 per day. A network producing defects at that rate is one
                where potholes routinely form between inspections — exactly the scenario where prior reports
                and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Forecast: 8,380 More</h3>
              <p className="text-amber-800 text-sm">
                The council estimates approximately 8,380 pothole repairs in 2025/26 — based on the average
                of the last five financial years. The 2025/26 carriageway programme splits
                <strong> 80% preventative</strong> surface dressing against <strong>20% reactive</strong>
                resurfacing, but routine reactive maintenance will continue as required.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Preventative maintenance aims to benefit the overall carriageway condition scores, reduce the need for reactive surfacing and pothole filling."
            source="Isle of Wight Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Geological and climate risks */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Geological Risk and Reactive Spend
          </h2>
          <p className="text-surface-600 mb-8">
            Beyond routine maintenance, the council is spending reactively on landslips and cliff falls —
            expenditure not captured in the PFI Unitary Charge breakdown
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="In addition to the delivery of the Output Specification of the Highways PFI Contract, and the agreed capital program, Geological events on the south of the island have required the Authority to spend reactively to ensure network resilience."
              source="Isle of Wight Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Location</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Issue</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Leeson Road, Ventnor</td>
                  <td className="px-6 py-4 text-surface-600">Landslip in December 2023 — temporary closure, ground investigation and new monitoring system</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Gill Cliff Road, Ventnor</td>
                  <td className="px-6 py-4 text-surface-600">Land movement and localised cliff fall — stabilisation via anchoring and netting</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Belgrave Road</td>
                  <td className="px-6 py-4 text-surface-600">Recent rockfall — plan to remove debris and reopen the network safely</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Over £1.0 Million Projected</h3>
              <p className="text-surface-600 text-sm">
                Current projected expenditure on these geological works exceeds <strong>£1.0 million</strong>,
                and the council notes future costs cannot definitively be predicted due to the nature of the works.
                This reactive spend sits outside the published PFI Unitary Charge figures.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Climate Risks Documented</h3>
              <p className="text-amber-800 text-sm">
                The council identifies coastal erosion, geological landslip risk, increased traffic demand,
                modal shift and more intense rainfall affecting highway drainage as network resilience challenges.
                If your incident was on a south-coast route near known geotechnical risk sites, that documented
                knowledge raises the standard for what a reasonable inspection regime looks like.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2025/26 Plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2025/26: Surface Dressing and Resurfacing Programme
          </h2>
          <p className="text-surface-600 mb-8">
            Planned works from the council&apos;s transparency report — preventative surface dressing dominates,
            with named streets across the island
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">16km</div>
              <div className="text-sm text-surface-600">Surface dressing (preventative)</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">3.96km</div>
              <div className="text-sm text-surface-600">Reactive resurfacing</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">28.40km</div>
              <div className="text-sm text-surface-600">Footway treatments</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">17</div>
              <div className="text-sm text-surface-600">Bridges and retaining walls</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">80% Preventative, 20% Reactive</h3>
              <p className="text-emerald-800 text-sm">
                The 2025/26 carriageway programme is split into 80% preventative surface dressing and 20%
                reactive resurfacing following condition survey outputs. Named surface dressing locations include
                High Street Niton, Victoria Road Freshwater, Seaview Road Cowes and Bowcombe Road Carisbrooke.
                Named resurfacing locations include Heathfield Road Freshwater, Parkhurst Road Newport and
                Zig Zag Road Ventnor.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">ISO 55001 Aspiration</h3>
              <p className="text-surface-600 text-sm">
                The council has an aspiration to achieve BS ISO 55001 Asset Management accreditation. Processes
                already in place include regular internal audits, annual reviews with survey providers, validation
                of survey data and network inventory reviews. Asset performance is monitored via deterioration
                modelling and walk-talk-build conclusions — but accreditation is not yet confirmed.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Plans Are Not Proof For Your Claim</h3>
            <p className="text-brand-200">
              A published list of 2025/26 surface dressing and resurfacing streets tells you where the council
              <em> plans</em> to work — not whether your specific road was maintained before your incident. If your
              pothole was on a road not named in the programme, that gap is worth citing. If it was on a listed
              street, check whether works were actually delivered before the date of your damage.
            </p>
          </div>
        </div>
      </section>

      {/* Claims balance */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Condition PFI Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: the Isle of Wight is not Derbyshire — here is how the Island Roads model changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — RED shares below England averages network-wide
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 559km rebuilt or resurfaced since 2013 under the PFI rehabilitation programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual SCANNER/SCRIM surveys and documented Highways Asset Management Plan
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> PFI output specifications with defined maintenance standards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Island Roads FixMyStreet system creates a dated notice trail
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing PFI inspection regimes and survey data. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall and AMBER best practice — not a model authority
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No published spend data — impossible to verify investment against DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED up 22-fold from 2020 baseline (0.2% → 4.4%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Footway surveys cover only half the network per year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 41,901 pothole fills in five years — defects still forming at scale
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £1.0m+ reactive geological spend diverting resources from routine maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ISO 55001 accreditation is aspiration, not yet achieved
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN condition scorecard and a formal PFI contract, your claim lives or dies on the
              <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (Island Roads FixMyStreet, fms.islandroads.com) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the 2020–2022 RED surge is your strongest structural argument</li>
              <li>• Whether your road was on the 2025/26 surface dressing or resurfacing programme — and whether works happened before your incident</li>
              <li>• Proximity to documented geological risk sites on the south coast</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites the Isle of Wight&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Island Roads first
          </h2>
          <p className="text-surface-600 mb-6">
            Island Roads maintains the highway network on behalf of Isle of Wight Council under the PFI contract.
            Reporting through the council&apos;s FixMyStreet system creates a dated record — useful evidence if the
            pothole was reported before your incident, or if Island Roads failed to repair it within a reasonable time.
          </p>
          <a
            href="https://fms.islandroads.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole via Island Roads FixMyStreet
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            For highway emergencies call Island Roads on 01983 822440. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole on the Isle of Wight?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A PFI council with bundled payments demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road RED surge argument</li>
                <li>• No prior-report search</li>
                <li>• No PFI spend-transparency gap cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED rise documented (0.2% → 4.4%)</li>
                <li>✅ Island Roads PFI spend gap argued</li>
                <li>✅ 41,901 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Isle of Wight</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=isle-of-wight"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Isle of Wight Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The Isle of Wight has a GREEN condition rating — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Condition scorecard is GREEN because network-wide RED shares sit below England
                averages — but your claim turns on the specific defect that damaged your vehicle. The council&apos;s
                own data shows U-road RED condition climbing from 0.2% in 2020 to 4.4% in 2022, and 9,715 potholes
                were filled in 2024/25 alone. Section 58 depends on whether your road was reasonably inspected and
                the defect repaired in time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is there no DfT Spend rating for the Isle of Wight?
              </h3>
              <p className="text-surface-600 text-sm">
                Highways maintenance runs through a 25-year Highways PFI Contract with Island Roads Services Ltd,
                which began on 1 April 2013. The council pays a single aggregated monthly Unitary Charge covering
                street lighting, cleaning, resurfacing, pothole repairs and dozens of other services. Ringway Island
                Roads expenditure is commercially sensitive and no capital or revenue breakdown is published. The
                DfT cannot score spend against allocation when no comparable figures exist.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 419.5km — 51.2% of the Isle of Wight&apos;s 819.48km adopted road network.
                Unclassified RED condition rose from 0.2% in 2020 to 4.4% in 2022 before settling at 3.0% in
                2024. SCANNER and SCRIM surveys run annually on carriageways where possible, with Detailed Visual
                Inspection covering roads not surveyed by SCANNER. Prior reports through Island Roads&apos;
                FixMyStreet system are critical evidence of actual notice.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Island Roads filled nearly 10,000 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The council recorded 9,715 pothole repairs in 2024/25 — up from 7,090 in 2022/23 and peaking
                at 10,140 in 2023/24. Over five years the total is 41,901 fills. The council forecasts 8,380 repairs
                in 2025/26. That volume is evidence of a network still producing defects at scale, even after 559km
                of carriageway and footway has been rebuilt or resurfaced since 2013.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the Island Roads PFI contract help or hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Both, depending on your road. The PFI imposes output specifications, annual surveys and a documented
                Highways Asset Management Plan — a structured system the council can cite under Section 58. But the
                council cannot break out pothole repair spend from a £726 million bundled contract, and reactive
                geological works on Leeson Road, Gill Cliff Road and Belgrave Road have already exceeded £1.0 million
                in projected expenditure. Bundled PFI payments weaken public transparency on whether your specific
                road received adequate reactive maintenance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Who do I claim against — the council or Island Roads?
              </h3>
              <p className="text-surface-600 text-sm">
                Liability rests with Isle of Wight Council as the highway authority under the Highways Act 1980.
                Island Roads and Ringway Island Roads maintain the network under contract, but the council remains
                legally responsible for Section 41 maintenance duties. Island Roads publishes its own claims form,
                but your statutory claim is against the council. A professional claim pack addresses the council and
                cites the council&apos;s own transparency report data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.iow.gov.uk/transport-and-parking/highways/local-highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Isle of Wight Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
